<div class="bg-white rounded-2xl overflow-hidden flex flex-col max-h-[90vh]">
  
  <div class="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
    <h2 class="text-xl font-bold text-slate-800">
      {{ isEditMode ? 'Редактиране на служител' : 'Добавяне на служител' }}
    </h2>
    <button (click)="dialogRef.close()" class="text-slate-400 hover:text-slate-600 transition-colors">
      <lucide-icon name="x" class="w-5 h-5"></lucide-icon>
    </button>
  </div>

  <div class="p-6 overflow-y-auto">
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-5">
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="space-y-1.5">
          <label class="text-sm font-medium text-slate-700">Потребителско име *</label>
          <input formControlName="username" 
                 class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                 [class.border-red-500]="f['username'].invalid && f['username'].touched"
                 placeholder="ivan_petrov">
          
          @if (f['username'].invalid && f['username'].touched) {
            <p class="text-xs text-red-500">Минимум 3 символа.</p>
          }
        </div>

        <div class="space-y-1.5">
          <label class="text-sm font-medium text-slate-700">Имейл *</label>
          <input formControlName="email" type="email"
                 class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                 [class.border-red-500]="f['email'].invalid && f['email'].touched"
                 placeholder="ivan@example.com">
        </div>
      </div>

      <div class="space-y-1.5">
        <label class="text-sm font-medium text-slate-700">
          {{ isEditMode ? 'Нова парола (оставете празно ако няма промяна)' : 'Парола *' }}
        </label>
        <div class="relative">
          <input [type]="showPassword ? 'text' : 'password'" 
                 formControlName="password"
                 class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all pr-10"
                 [class.border-red-500]="(f['password'].invalid && f['password'].touched) || (passwordErrors.length > 0 && f['password'].dirty)"
                 placeholder="******">
          
          <button type="button" (click)="togglePasswordVisibility()" 
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <lucide-icon [name]="showPassword ? 'eye-off' : 'eye'" class="w-4 h-4"></lucide-icon>
          </button>
        </div>
      </div>

      @if (passwordErrors.length > 0 && f['password'].dirty) {
        <div class="bg-red-50 border border-red-200 rounded-xl p-3">
          <p class="font-semibold text-red-900 mb-2 text-xs">Изисквания за паролата:</p>
          <ul class="list-disc list-inside space-y-1 text-xs text-red-700">
            @for (error of passwordErrors; track error) {
              <li>{{ error }}</li>
            }
          </ul>
        </div>
      }

      @if (!isEditMode || f['password'].value) {
        <div class="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <p class="font-semibold text-blue-900 mb-2 text-xs">Паролата трябва да съдържа:</p>
          <ul class="list-disc list-inside space-y-1 text-xs text-blue-700">
            <li>Поне 8 символа</li>
            <li>Главни и малки букви</li>
            <li>Цифри (0-9)</li>
            <li>Специални символи (~!&#64;#$%^&*_-+=`|(){{ '{' }}]{{ '}' }}:;"'<>,.?/)</li>
          </ul>
        </div>
      }

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        <div class="space-y-1.5">
          <label class="text-sm font-medium text-slate-700">Роля *</label>
          <select formControlName="roleId"
                  class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none">
            @for (role of roles; track role.id) {
              <option [value]="role.id">{{ role.name }}</option>
            }
          </select>
        </div>

        <div class="space-y-1.5">
          <label class="text-sm font-medium text-slate-700">Ниво на опит *</label>
          <select formControlName="experienceLevelId"
                  class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none">
            @for (level of experienceLevels; track level.id) {
              <option [value]="level.id">{{ level.name }}</option>
            }
          </select>
        </div>
      </div>

      <div *ngIf="isEditMode" class="pt-2 border-t border-slate-100">
        <div class="flex items-center gap-2">
          <input 
            type="checkbox" 
            id="isActive" 
            formControlName="isActive"
            class="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
          >
          <label htmlFor="isActive" class="text-sm font-medium text-slate-700 cursor-pointer select-none">
            Служителят е активен
          </label>
        </div>
        <p class="text-xs text-slate-500 mt-1 pl-6">
          Неактивните служители нямат достъп до системата, но историята им се запазва.
        </p>
      </div>

    </form>
  </div>

  <div class="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
    <button type="button" (click)="dialogRef.close()" 
            class="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-white border border-transparent hover:border-slate-200 rounded-xl transition-all">
      Отказ
    </button>
    
    <button type="button" (click)="onSubmit()" 
            [disabled]="form.invalid || isLoading || passwordErrors.length > 0"
            class="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-xl shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
      @if (isLoading) {
        <lucide-icon name="loader-2" class="w-4 h-4 mr-2 animate-spin"></lucide-icon>
      }
      {{ isEditMode ? 'Запази промените' : 'Създай служител' }}
    </button>
  </div>

</div>